import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, ArrowLeft } from 'lucide-react';

const SynonymDetail = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [domain, setDomain] = useState({
        domain_id: '',
        description: '',
        domain_type: 'SYNONYM',
        values: []
    });

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    // 1. دالة استرجاع البيانات عند التعديل
    useEffect(() => {
        if (id && id !== 'new' && id !== 'undefined') {
            const loadId = toast.loading(t('domains.toasts.loading'));
            axios.get(`/api/domains/${id}`)
                .then(res => {
                    setDomain(res.data);
                    toast.dismiss(loadId);
                })
                .catch(err => {
                    toast.error(t('domains.toasts.load_fail'), { id: loadId });
                });
        }
    }, [id, t]);

    // 2. دالة الحفظ والتعديل
    const handleSave = async () => {
        const domainId = String(domain.domain_id || "").trim();
        if (domainId.length === 0) return toast.error(t('domains.toasts.id_required'));

        const loadId = toast.loading(t('domains.toasts.saving'));
        try {
            await axios.post('/api/domains/save', domain);
            toast.success(t('domains.toasts.save_success'), { id: loadId });
            
            if (id === 'new') {
                navigate(`/domains/${domainId}`);
            }
        } catch (err) {
            toast.error(t('domains.toasts.save_fail') + ": " + (err.response?.data?.detail || ""), { id: loadId });
        }
    };

    const addValueRow = () => {
        setDomain({
            ...domain,
            values: [...domain.values, { value: '', description: '', is_default: false }]
        });
    };

    const removeValueRow = (idx) => {
        const newVals = domain.values.filter((_, i) => i !== idx);
        setDomain({ ...domain, values: newVals });
    };

    return (
        <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`}>
            {/* Toolbar */}
            <div className={`bg-white p-2 border-b border-gray-300 flex items-center justify-between shadow-sm `} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className={`flex items-center gap-4 ${isRTL ? 'mr-4' : 'ml-4'} `}>
                    <button onClick={() => navigate('/domains')} className="text-gray-500 hover:text-blue-700">
                        <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
                    </button>
                    <div className={`flex items-center gap-2`}>
                        <label className="text-gray-500 font-bold uppercase tracking-tighter">{t('domains.domain_id')}</label>
                        <input 
                            className="border-b border-dashed border-gray-400 w-32 outline-none font-bold text-blue-900 bg-transparent uppercase"
                            value={domain.domain_id}
                            disabled={id !== 'new' && id !== undefined} 
                            onChange={(e) => setDomain({...domain, domain_id: e.target.value.toUpperCase()})}
                        />
                        <input 
                            className={`border-b border-gray-400 w-[500px] outline-none text-blue-800 font-bold bg-transparent`}
                            placeholder={t('domains.desc_placeholder')}
                            value={domain.description || ''}
                            onChange={(e) => setDomain({...domain, description: e.target.value})}
                        />
                    </div>
                </div>
                <button onClick={handleSave} className="flex items-center gap-2 bg-[#3c70a4] text-white px-6 py-1.5 rounded font-bold hover:bg-[#2a5078] transition-all shadow-md">
                    <Save size={14}/> {t('domains.save_btn')}
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-white border border-gray-300 shadow-sm max-w-6xl mx-auto">
                    <div className={`p-2 bg-gray-100 border-b flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="font-bold text-gray-700 uppercase">{t('domains.synonym_values')}</span>
                        <button onClick={addValueRow} className="flex items-center gap-1 border border-blue-400 px-3 py-1 bg-white text-blue-700 font-bold hover:bg-blue-50">
                            <Plus size={12}/> {t('domains.new_row')}
                        </button>
                    </div>
                    
                    <table className="w-full border-collapse">
                        <thead className="bg-[#f8f9fa] border-b text-gray-500 font-bold uppercase">
                            <tr className={`${isRTL ? 'flex-row-reverse' : ''}`}>
                                <th className="p-2 border-r w-12 text-center">#</th>
                                <th className={`p-2 border-r w-64 ${isRTL ? 'text-right' : 'text-left'}`}>{t('domains.internal_val')}</th>
                                <th className={`p-2 border-r ${isRTL ? 'text-right' : 'text-left'}`}>{t('domains.external_desc')}</th>
                                <th className="p-2 border-r text-center w-24">{t('domains.default')}</th>
                                <th className="p-2 w-12 text-center">{t('domains.action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {domain.values?.map((v, idx) => (
                                <tr key={idx} className={`border-b hover:bg-blue-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <td className="p-2 border-r text-center text-gray-400">{idx + 1}</td>
                                    <td className="p-2 border-r">
                                        <input 
                                            className={`w-full bg-transparent outline-none font-bold uppercase text-blue-900 ${isRTL ? 'text-right' : 'text-left'}`}
                                            value={v.value || ''}
                                            onChange={(e) => {
                                                const newVals = [...domain.values];
                                                newVals[idx].value = e.target.value.toUpperCase();
                                                setDomain({...domain, values: newVals});
                                            }}
                                        />
                                    </td>
                                    <td className="p-2 border-r">
                                        <input 
                                            className={`w-full bg-transparent outline-none ${isRTL ? 'text-right' : 'text-left'}`}
                                            value={v.description || ''}
                                            onChange={(e) => {
                                                const newVals = [...domain.values];
                                                newVals[idx].description = e.target.value;
                                                setDomain({...domain, values: newVals});
                                            }}
                                        />
                                    </td>
                                    <td className="p-2 border-r text-center">
                                        <input 
                                            type="checkbox" 
                                            checked={v.is_default || false}
                                            onChange={(e) => {
                                                const newVals = [...domain.values];
                                                newVals[idx].is_default = e.target.checked;
                                                setDomain({...domain, values: newVals});
                                            }}
                                            className="w-4 h-4 cursor-pointer"
                                        />
                                    </td>
                                    <td className="p-2 text-center">
                                        <button onClick={() => removeValueRow(idx)} className="text-red-500 hover:scale-110 transition-transform">
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {(!domain.values || domain.values.length === 0) && (
                                <tr>
                                    <td colSpan="5" className="p-10 text-center text-gray-400 italic">{t('domains.no_values')}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SynonymDetail;